import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchOrderStats,
  fetchAppointmentStats,
  fetchMessageStats,
  fetchRequestsStats,
} from 'app/shared-components/store/pharmacySelectorSlice';
import { updateNavigationBadge } from 'app/store/fuse/navigationSlice';
import { DEPLOYMENT_REGIONS } from 'app/constants';
import useOrderStatusCounts from 'app/pages/OrdersV2/use-cases/OrdersListView/hooks/useOrderStatusCounts';

import config from '../../config.json';

const isCanada = config.deploymentRegion === DEPLOYMENT_REGIONS.CA;

export default function usePharmacyStats() {
  const dispatch = useDispatch();
  const { newCount, pendingCount } = useOrderStatusCounts();

  const {
    selectedPharmacyId,
    selectedPharmacy,
    pharmacyStats: {
      orders: { overdue, overdueDMRs },
      requests: { totalRequestsCount },
      appointments: { count: requestedAppointmentsCount },
      messages: { patients: { unreadMessages: unreadMessagesCount } = {} },
    },
  } = useSelector((state) => state.pharmacySelector);

  const isFreePharmacy = selectedPharmacy?.status === 'free';

  useEffect(() => {
    if (selectedPharmacyId) {
      // Only the Canada sidebar counts requested appointments.
      if (isCanada) dispatch(fetchAppointmentStats());

      if (isFreePharmacy) return;
      if (!isCanada) dispatch(fetchOrderStats());
      if (isCanada) dispatch(fetchRequestsStats());
      dispatch(fetchMessageStats());
    }
  }, [dispatch, selectedPharmacyId, isFreePharmacy]);

  // The UK sidebar counts Attention, Patients and overdue managed repeats; Canada counts Patients,
  // orders and calendar.
  // Counts are passed on even when zero, so a cleared count drops its badge.
  useEffect(() => {
    if (!isCanada) return;
    dispatch(updateNavigationBadge('orders-component', newCount + pendingCount));
  }, [dispatch, newCount, pendingCount]);

  useEffect(() => {
    dispatch(updateNavigationBadge('overdue-orders', overdue));
  }, [dispatch, overdue]);

  useEffect(() => {
    if (isCanada) return;
    dispatch(updateNavigationBadge('managed-repeats', overdueDMRs));
  }, [dispatch, overdueDMRs]);

  useEffect(() => {
    if (!isCanada) return;
    dispatch(updateNavigationBadge('calendar', requestedAppointmentsCount));
  }, [dispatch, requestedAppointmentsCount]);

  useEffect(() => {
    dispatch(updateNavigationBadge('patients-component', unreadMessagesCount));
  }, [dispatch, unreadMessagesCount]);

  useEffect(() => {
    if (!isCanada) return;
    dispatch(updateNavigationBadge('requests-component', totalRequestsCount));
  }, [dispatch, totalRequestsCount]);
}
